import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getCurrWeekList, formateDate, minixTimeAndClass, preMix, weeksBetweenDates, generateWeeksData } from '../utils/scheduleUtils';
import { TimeTableType } from '../types/schedule';
import './ScheduleGrid.css';

// 生成“荧光系”RGB字符串的工具
function hashString(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
  }
  return Math.abs(h >>> 0);
}

function hslToRgbString(h, s, l) {
  // h:[0,360) s,l:[0,100]
  s /= 100; l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (0 <= h && h < 60) { r = c; g = x; b = 0; }
  else if (60 <= h && h < 120) { r = x; g = c; b = 0; }
  else if (120 <= h && h < 180) { r = 0; g = c; b = x; }
  else if (180 <= h && h < 240) { r = 0; g = x; b = c; }
  else if (240 <= h && h < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }
  const R = Math.round((r + m) * 255);
  const G = Math.round((g + m) * 255);
  const B = Math.round((b + m) * 255);
  return `${R}, ${G}, ${B}`;
}

// 固定的柔和浅色配色（与弹窗一致的浅色系），搭配加深使用
const PASTEL_RGB_PALETTE = [
  '173, 216, 230', // 浅蓝 LightBlue
  '193, 225, 193', // 雾绿 PaleGreen-ish
  '255, 224, 189', // 奶杏 Apricot/Almond
  '186, 245, 211', // 薄荷 Mint
  '221, 214, 250', // 雾紫 Lavender
  '210, 222, 255', // 雾蓝 Mist Blue
  '255, 245, 204', // 奶黄 Light Pastel Yellow
  '222, 239, 234', // 冰川青 Glacier
  '236, 223, 246', // 淡紫雾 Lilac Mist
  '255, 229, 217', // 桃奶 Peach Cream
];

function darkenRgbString(rgbStr, factor = 1) {
  // factor: 0~1，数值越小越暗
  const [r, g, b] = rgbStr.split(',').map(s => parseInt(s.trim(), 10));
  const dr = Math.max(0, Math.min(255, Math.round(r * factor)));
  const dg = Math.max(0, Math.min(255, Math.round(g * factor)));
  const db = Math.max(0, Math.min(255, Math.round(b * factor)));
  return `${dr}, ${dg}, ${db}`;
}

function getRandomFromPalette() {
  const idx = Math.floor(Math.random() * PASTEL_RGB_PALETTE.length);
  return PASTEL_RGB_PALETTE[idx];
}

const ScheduleGrid = ({ 
  startTime = '2023-8-28', 
  semester = {}, 
  timeTable = TimeTableType,
  enableWeekSwipe = false, // 是否启用横向滑动切换周次，默认关闭，避免与外层切课表冲突
  courseOpacity = 0.9,
  panelOpacity = 0.96,
  courseTextColor = '#0f1b2d',
}) => {
  const [currentWeek, setCurrentWeek] = useState(0);
  const [weekDayList, setWeekDayList] = useState([]);
  const [weekList, setWeekList] = useState([]);
  const [WeekListType, setWeekListType] = useState(true);
  const [renderData, setRenderData] = useState([]);
  const [theDay, setTheDay] = useState('');
  const [setTime, setSetTime] = useState('');
  const [isShow, setIsShow] = useState(false);
  const [current, setCurrent] = useState({});
  const colorMap = useRef(new Map()); // 课程名 -> RGB 字符串
  const paletteSeed = useRef(Math.random().toString(36).slice(2)); // 用于本会话配色随机种子（映射键的一部分）
  const [dialogVisible, setDialogVisible] = useState(false); // 控制挂载/卸载
  // 首次引导“点此可以切换周”
  const [showWeekHint, setShowWeekHint] = useState(false);
  const [hintPos, setHintPos] = useState({ top: 0, left: 0 });
  const weekBtnRef = useRef(null);
  // 触摸滑动状态
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const swiping = useRef(false);
  // 每次进入页面（组件挂载）重置颜色缓存；保留新种子，使得每次进入颜色会变，但同一会话内稳定
  useEffect(() => {
    colorMap.current = new Map();
    paletteSeed.current = Math.random().toString(36).slice(2);
  }, []);

  // 首次进入班级课表时展示一次周切换提示
  useEffect(() => {
    try {
      const flag = localStorage.getItem('weekHintShown');
      if (flag !== '1') {
        setShowWeekHint(true);
        localStorage.setItem('weekHintShown', '1');
      }
    } catch (e) {
      // 忽略本地存储异常
      setShowWeekHint(true);
    }
  }, []);

  // 计算引导位置：定位在“第N周”按钮右侧中点（用于右侧箭头指向左边的布局）
  useEffect(() => {
    if (!showWeekHint) return;
    const el = weekBtnRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      // 将提示组定位到按钮右侧中点，向右偏移 30px，垂直居中
      setHintPos({ top: rect.top + rect.height / 2, left: rect.right + 90 });
    }
    const onResize = () => {
      if (!weekBtnRef.current) return;
      const r = weekBtnRef.current.getBoundingClientRect();
      setHintPos({ top: r.top + r.height / 2, left: r.right + 90 });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [showWeekHint]);

  // 首次引导：显示后 5s 自动消失（用户也可点击遮罩立即关闭）
  useEffect(() => {
    if (!showWeekHint) return;
    const timer = setTimeout(() => setShowWeekHint(false), 5000);
    return () => clearTimeout(timer);
  }, [showWeekHint]);

  // 打开弹窗时禁止页面滚动
  useEffect(() => {
    if (!dialogVisible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [dialogVisible]);

  // 初始化
  useEffect(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    const day = today.getDate();
    const date = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;

    setTheDay(date);

    if (startTime) {
      setWeekList(generateWeeksData(startTime));
      // 如果今天在学期开始之前，默认显示第1周（学期开始日期那一周）
      const startDate = new Date(startTime.replace(/-/g, '/'));
      if (today < startDate) {
        setSetTime(startTime); // 默认选中学期开始日期
      } else {
        setSetTime(date);
      }
    } else {
      setSetTime(date);
    }
  }, [startTime]);

  // 填充本周列表
  const fullweekDayList = React.useCallback(() => {
    const time = new Date(setTime.replace(/-/g, "/") || new Date());
    const list = getCurrWeekList(time);
    const newWeekDayList = [];
    
    list.forEach(item => {
      newWeekDayList.push({
        day: [item.split('-')[1], item.split('-')[2]].join('-'),
        week: "星期" + "日一二三四五六".charAt((new Date(item.replace(/-/g, "/"))).getDay()),
        isCurr: formateDate(time) === item
      });
    });
    
    setWeekDayList(newWeekDayList);
  }, [setTime]);

  // 混合渲染表数据
  const minixData = React.useCallback(() => {
    const processedSchedule = preMix(currentWeek, semester);
    const newRenderData = minixTimeAndClass(timeTable, processedSchedule);
    setRenderData(newRenderData);
  }, [currentWeek, semester, timeTable]);

  // 监听数据变化
  useEffect(() => {
    fullweekDayList();
  }, [fullweekDayList]);

  useEffect(() => {
    if (startTime && setTime) {
      const newCurrentWeek = weeksBetweenDates(startTime, setTime || new Date());
      setCurrentWeek(newCurrentWeek);
    }
  }, [startTime, setTime]);

  useEffect(() => {
    if (currentWeek > 0) {
      minixData();
    }
  }, [minixData, currentWeek]);

  // 事件处理
  const getDetail = (item) => {
    // 计算并附着同色系到详情，确保弹窗颜色与课程块一致
    const keyName = item?.sub || '课程';
    const mapKey = `${paletteSeed.current}:${keyName}`; // 同一会话稳定，不同会话随机
    let rgb = colorMap.current.get(mapKey);
    if (!rgb) {
      // 不加深，直接使用柔和色板本色
      rgb = getRandomFromPalette();
      colorMap.current.set(mapKey, rgb);
    }
    setCurrent({ ...item, _rgb: rgb });
    setDialogVisible(true);
    // 下一帧切换 show，触发进入动画
    requestAnimationFrame(() => setIsShow(true));
  };

  const toggleWeekListType = () => {
    setWeekListType(!WeekListType);
  };

  const reSetData = () => {
    setWeekListType(true);
    setSetTime(theDay);
  };

  const closeMask = () => {
    // 先触发退出动画
    setIsShow(false);
    // 动画结束后卸载
    setTimeout(() => setDialogVisible(false), 220);
  };

  const chooseWeek = (date) => {
    setSetTime(date);
    setWeekListType(true); // 选择周后自动切换回星期显示
  };

  // 横向滑动切换周次（移动端手势）- 仅在头部周/星期区域启用，且阻止事件冒泡
  const handleTouchStart = (e) => {
    if (!enableWeekSwipe) return;
    if (!e.touches || e.touches.length === 0) return;
    e.stopPropagation();
    const t = e.touches[0];
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
    swiping.current = true;
  };

  const handleTouchMove = (e) => {
    if (!enableWeekSwipe) return;
    e.stopPropagation();
    // 可根据需要阻止默认滚动；此处仅记录，不阻止
  };

  const handleTouchEnd = (e) => {
    if (!enableWeekSwipe) return;
    if (!swiping.current) return;
    e.stopPropagation();
    swiping.current = false;
    const touch = (e.changedTouches && e.changedTouches[0]) || null;
    if (!touch) return;
    const dx = touch.clientX - touchStartX.current;
    const dy = touch.clientY - touchStartY.current;
    // 横向滑动阈值
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    if (absX < 50 || absY > 60) return; // 需要明显的横向滑动，且排除纵向滚动

    // 计算当前周索引
    const idx = weekList.findIndex((w) => w.data === setTime);
    if (idx === -1) return;
    let targetIdx = idx;
    if (dx < 0) {
      // 向左滑：下一周
      targetIdx = Math.min(weekList.length - 1, idx + 1);
    } else if (dx > 0) {
      // 向右滑：上一周
      targetIdx = Math.max(0, idx - 1);
    }
    if (targetIdx !== idx) {
      setSetTime(weekList[targetIdx].data);
      setWeekListType(true);
    }
  };

  return (
    <div
      className="schedule"
    >
      {/* 头部网格 */}
      <div
        className="container mb20"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* 左侧第N周按钮 */}
        <div className="container-left" style={{ background: `rgba(255,255,255, ${Math.max(0.6, Math.min(1, Number(panelOpacity) || 0.96))})` }}>
          <div className="time-slot week-button" onClick={toggleWeekListType} ref={weekBtnRef}>
            <span className="con-title">第</span>
            <span className="week-number">{currentWeek}</span>
            <span className="week-text">周</span>
          </div>
        </div>
        
        {/* 右侧星期/周选择网格 */}
        <div className="container-right" style={{ background: `rgba(255,255,255, ${Math.max(0.6, Math.min(1, Number(panelOpacity) || 0.96))})` }}>
          {/* 同时挂载，使用过渡类切换 */}
          <div className={`week-view week-days-container ${WeekListType ? 'show' : 'noShow'}`}>
            {weekDayList.map((item, index) => (
              <div key={index} className="week-grid-item">
                <span className={`week-name ${item.isCurr ? 'current-day' : ''}`}>{item.week}</span>
                <span className={`week-date ${item.isCurr ? 'current-day' : ''}`}>{item.isCurr ? '今天' : item.day}</span>
              </div>
            ))}
          </div>

          <div className={`week-view weekChooseList ${WeekListType ? 'noShow' : 'show'}`} style={{ gridColumn: '1 / -1', width: '100%' }}>
            {weekList.map((item, index) => (
              <div
                key={index}
                className={`week-grid-item ${item.data === setTime ? 'current-week' : ''}`}
                onClick={() => chooseWeek(item.data)}
              >
                <span className="week-name">第</span>
                <span className="week-date">{item.index}</span>
                <span className="week-name">周</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 课表数据 */}
      {renderData.map((dayChunk, dayIndex) => (
        <div key={dayIndex} className={`container mb20 s${dayChunk.group}`}>
          {/* 左侧时间 */}
          <div className={`container-left s${dayChunk.group}`} style={{ background: `rgba(255,255,255, ${Math.max(0.6, Math.min(1, Number(panelOpacity) || 0.96))})` }}>
            {dayChunk.time.map((timeChunk, timeIndex) => (
              <div key={timeIndex} className="time-slot">
                <span className="con-title">{timeChunk.index}</span>
                <span>{timeChunk.timeStart}</span>
                <span>{timeChunk.timeEnd}</span>
              </div>
            ))}
          </div>

          {/* 右侧课表 */}
          <div className={`container-right s${dayChunk.group}`} style={{ background: `rgba(255,255,255, ${Math.max(0.6, Math.min(1, Number(panelOpacity) || 0.96))})` }}>
            {Object.entries(dayChunk.schedule).map(([day, classChunkOfDay]) => {
              // 每两节课合并为一个容器
              const containers = [];
              
              for (let i = 0; i < classChunkOfDay.length; i += 2) {
                const firstCourse = classChunkOfDay[i];
                const secondCourse = classChunkOfDay[i + 1];
                
                // 检查课程是否有效（不是数字0且有课程名）
                const isValidFirstCourse = firstCourse && typeof firstCourse === 'object' && firstCourse.sub;
                const isValidSecondCourse = secondCourse && typeof secondCourse === 'object' && secondCourse.sub;
                
                containers.push({
                  firstRow: isValidFirstCourse ? firstCourse : null,
                  secondRow: isValidSecondCourse ? secondCourse : null,
                  size: 2
                });
              }
              
              // 每个容器都在同一行显示，不分两行
              const allContainers = containers;
              
              return (
                <div key={day} className="aday">
                  {allContainers.map((container, containerIndex) => {
                    const isValidFirstCourse = container.firstRow && typeof container.firstRow === 'object' && container.firstRow.sub;
                    const isValidSecondCourse = container.secondRow && typeof container.secondRow === 'object' && container.secondRow.sub;
                    const displayCourse = isValidFirstCourse ? container.firstRow : (isValidSecondCourse ? container.secondRow : null);

                    // 计算课程块背景色（同一会话稳定，不同会话随机），不加深
                    const styleObj = (() => {
                      if (!displayCourse) return { background: 'transparent', flex: 1, minHeight: '100%' };
                      const keyName = displayCourse.sub || '课程';
                      const mapKey = `${paletteSeed.current}:${keyName}`; // 会话稳定
                      let rgb = colorMap.current.get(mapKey);
                      if (!rgb) {
                        rgb = getRandomFromPalette();
                        colorMap.current.set(mapKey, rgb);
                      }
                      const darkened = darkenRgbString(rgb, 0.9); // 稍微加深，保证可视度但不过深
                      const alpha = Math.max(0.4, Math.min(1, Number(courseOpacity) || 0.9));
                      return { background: `rgba(${darkened}, ${alpha})`, flex: 1, minHeight: '100%' };
                    })();
                    return (
                      <div key={`container-${containerIndex}`} className="container-wrapper">
                        <div
                          className={`con-item mb10 size2 appear`}
                          style={{
                            ...styleObj,
                            color: courseTextColor || '#0f1b2d',
                            animationDelay: `${(containerIndex % 6) * 40}ms`,
                          }}
                          onClick={displayCourse ? () => getDetail(displayCourse) : undefined}
                        >
                          {displayCourse && (
                            <>
                              <span className="con-item-subj line_ellipsis">{displayCourse.sub}</span>
                              <span className="line_ellipsis">{displayCourse.tec}</span>
                              <span className="line_ellipsis">{displayCourse.add}</span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* 详情弹框：使用 Portal，保持挂载以完成退场动画 */}
      {dialogVisible && createPortal(
        (
          <div
            className={`schedule-modal ${isShow ? 'show' : ''}`}
            onClick={closeMask}
          >
            <div
              className={`simple-dialog ${isShow ? 'enter-anim' : 'leave-anim'}`}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: current?._rgb ? `rgba(${current._rgb}, 0.95)` : undefined,
                color: '#fff',
                border: '1px solid rgba(0,0,0,0.12)',
                boxShadow: '0 14px 32px rgba(0,0,0,0.28)',
                textShadow: '0 1px 0 rgba(0,0,0,0.12)',
              }}
            >
              <div>{current.sub}</div>
              <div>{current.add}</div>
              <div>{current.tec}</div>
              <div>{current.week}({currentWeek})</div>
            </div>
          </div>
        ),
        document.body
      )}

      {/* 首次引导遮罩：指向“第N周”按钮 */}
      {showWeekHint && createPortal(
        (
          <div className="week-hint-mask" onClick={() => setShowWeekHint(false)}>
            {/* 将箭头与文字包在同一个定位容器内：右侧箭头、向左指，文字在箭头右边 */}
            <div
              className="week-hint-group"
              style={{ left: hintPos.left + 'px', top: hintPos.top + 'px', transform: 'translateY(-50%)' }}
            >
              <div className="week-hint-arrow rtl" />
              <div className="week-hint-bubble">点此可以切换周</div>
            </div>
          </div>
        ),
        document.body
      )}
    </div>
  );
};

export default ScheduleGrid;
